/**
 * AbilityDispatcher — Single Responsibility: enrutar comandos de habilidades
 * al handler correcto por campeón usando el patrón Strategy.
 *
 * CUMPLE OCP: kits se registran vía `registerChampionDispatcher` (championKitBoot).
 * Seed Map vacío (AB-09 / P6-05 #5) — no kits inline.
 *
 * Contrato público:
 *   dispatchAbility(champKey, player, abilityKey, worldPos, projectiles, gameTime?) → DispatchResult | null
 *
 * DispatchResult = { type: string, projectiles: ProjectileSpec[], cooldown: number }
 */

// ─── Guard helpers ────────────────────────────────────────────────────────────

function canCast(player) {
  if (!player || player.isDead) return false;
  if (typeof player.canCastSkills === 'function') {
    return player.canCastSkills();
  }
  if (typeof player.statusManager?.canCastSkills === 'function') {
    return player.statusManager.canCastSkills();
  }
  return true;
}

// ─── Per-champion Strategy Map (populated by championKitBoot) ─────────────────

const CHAMPION_DISPATCHERS = new Map();

/**
 * Despacha una habilidad al campeón correcto.
 *
 * @param {string} champKey          - 'ashe' | 'garen' | 'ryze' | ...
 * @param {object} player            - Entity del jugador
 * @param {string} abilityKey        - 'Q' | 'W' | 'E' | 'R'
 * @param {{ x: number, y: number }} worldPos
 * @param {Array}  projectiles       - Array mutable de proyectiles de la escena
 * @param {number} [gameTime=0]
 * @param {Array}  [entities=[]]     - Todas las entidades (para AoE)
 * @param {object} [selectedTarget=null]
 * @returns {{ type: string, projectiles: Array, cooldown: number } | null}
 */
export function dispatchAbility(
  champKey,
  player,
  abilityKey,
  worldPos,
  projectiles,
  gameTime = 0,
  entities = [],
  selectedTarget = null
) {
  if (!canCast(player)) return null;

  const dispatcher = CHAMPION_DISPATCHERS.get(champKey?.toLowerCase());
  if (!dispatcher) return null;

  const handler = dispatcher[abilityKey];
  if (typeof handler !== 'function') return null;

  return handler(player, worldPos, projectiles, gameTime, entities, selectedTarget) ?? null;
}

/**
 * Registra un nuevo dispatcher de campeón (para extensión sin modificar este archivo).
 * @param {string} champKey
 * @param {Record<string, Function>} abilityHandlers  - e.g. { W: fn, R: fn }
 */
export function registerChampionDispatcher(champKey, abilityHandlers) {
  CHAMPION_DISPATCHERS.set(champKey.toLowerCase(), abilityHandlers);
}
